<template>
  <div class="tests-dropdown" ref="dropdownRef">
    <div class="test-label">{{ label }}:</div>
    <div class="tests-value" @click="toggleDropdown" ref="triggerRef">
      <span class="value-text">{{ truncatedText }}</span>
      <svg width="8" height="4" viewBox="0 0 8 4" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M0.646447 0.146447C0.451184 0.341709 0.451184 0.658291 0.646447 0.853553L3.64645 3.85355C3.84171 4.04882 4.15829 4.04882 4.35355 3.85355L7.35355 0.853554C7.54882 0.658292 7.54882 0.341709 7.35355 0.146447C7.15829 -0.0488153 6.84171 -0.0488153 6.64645 0.146447L4 2.79289L1.35355 0.146447C1.15829 -0.0488155 0.841709 -0.0488155 0.646447 0.146447Z" fill="#4A68F8"/>
      </svg>
    </div>

    <Teleport to="body">
      <div
          v-if="isOpen"
          class="dropdown-portal-container"
          :style="dropdownStyle"
          ref="portalRef"
      >

        <div class="dropdown-menu-portal">
          <div v-if="searchable" class="search-container">
            <input
                v-model="searchQuery"
                type="text"
                class="search-input"
                :placeholder="getPlaceholder()"
                @input="$emit('search', searchQuery)"
                @keydown.stop
                ref="searchInputRef"
            />
          </div>

          <div class="dropdown-items-container">
            <div
                v-for="option in options"
                :key="typeof option === 'object' ? option.value : option"
                class="dropdown-item"
                :class="{ 'active': (typeof option === 'object' ? option.value : option) === modelValue }"
                @click="selectOption(typeof option === 'object' ? option.value : option)"
            >
              <div class="dropdown-item-content">
                <img v-if="typeof option === 'object' && option.icon"
                     :src="option.icon"
                     class="dropdown-item-icon" />
                <span class="dropdown-item-text">
                  {{ typeof option === 'object' ? option.label : option }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  label: {
    type: String,
    default: 'Seçim'
  },
  modelValue: {
    type: [String, Number],
    default: ''
  },
  options: {
    type: Array,
    default: () => []
  },
  maxTextLength: {
    type: Number,
    default: 15
  },
  searchable: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue', 'search']);
const { t } = useI18n();

const isOpen = ref(false);
const selectedValue = ref(props.modelValue);
const searchQuery = ref('');
const dropdownRef = ref(null);
const triggerRef = ref(null);
const portalRef = ref(null);
const searchInputRef = ref(null);
const dropdownStyle = ref({});

const getPlaceholder = () => {
  try {
    return t && typeof t === 'function' ? t('search') : 'Ara...';
  } catch (error) {
    return 'Ara...';
  }
};

const selectedText = computed(() => {
  if (props.options.length === 0) return '';

  if (typeof props.options[0] === 'object') {
    const selected = props.options.find(opt =>
        (typeof opt === 'object' ? opt.value : opt) === props.modelValue
    );
    return selected ? selected.label : '';
  }

  return props.modelValue;
});

const truncatedText = computed(() => {
  if (!selectedText.value) return '';

  if (selectedText.value.length > props.maxTextLength) {
    return selectedText.value.substring(0, props.maxTextLength) + '...';
  }

  return selectedText.value;
});

const calculateDropdownPosition = () => {
  if (!triggerRef.value) return;

  const rect = triggerRef.value.getBoundingClientRect();

  dropdownStyle.value = {
    position: 'fixed',
    top: `${rect.bottom + 5}px`,
    left: `${rect.left}px`,
    minWidth: `${rect.width}px`,
    maxWidth: '300px',
    zIndex: '999999'
  };
};

function toggleDropdown() {
  isOpen.value = !isOpen.value;

  if (isOpen.value) {
    nextTick(() => {
      calculateDropdownPosition();

      const dropdown = document.querySelector('.dropdown-portal-container');
      if (dropdown) {
        dropdown.style.display = 'block';
        dropdown.style.visibility = 'visible';
        dropdown.style.opacity = '1';
        dropdown.style.position = 'fixed';
        dropdown.style.zIndex = '999999';
      }

      if (props.searchable && searchInputRef.value) {
        nextTick(() => {
          searchInputRef.value.focus();
        });
      }

      window.addEventListener('scroll', calculateDropdownPosition, { passive: true, capture: true });
      window.addEventListener('resize', calculateDropdownPosition, { passive: true });
      document.addEventListener('click', closeDropdown, { passive: true });
    });
  } else {
    removeEventListeners();
  }
}

// Arama kutusu temizlenirken üst bileşen de haberdar edilmeli; aksi hâlde
// liste, kutusu boş görünmesine rağmen eski aramayla filtreli kalıyor.
function resetSearch() {
  if (searchQuery.value === '') return;
  searchQuery.value = '';
  emit('search', '');
}

function selectOption(value) {
  selectedValue.value = value;
  emit('update:modelValue', value);
  isOpen.value = false;
  resetSearch();
  removeEventListeners();
}

function closeDropdown(e) {
  if (triggerRef.value && triggerRef.value.contains(e.target)) {
    return;
  }

  if (portalRef.value && portalRef.value.contains(e.target)) {
    return;
  }

  isOpen.value = false;
  resetSearch();
  removeEventListeners();
}

function removeEventListeners() {
  window.removeEventListener('scroll', calculateDropdownPosition, { passive: true, capture: true });
  window.removeEventListener('resize', calculateDropdownPosition, { passive: true });
  document.removeEventListener('click', closeDropdown, { passive: true });
}

function handleKeydown(e) {
  if (!isOpen.value) return;

  if (e.key === 'Escape') {
    isOpen.value = false;
    resetSearch();
    removeEventListeners();
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  removeEventListeners();
  document.removeEventListener('keydown', handleKeydown);
});

watch(() => props.modelValue, (newValue) => {
  selectedValue.value = newValue;
});
</script>

<style scoped>
.tests-dropdown {
  display: inline-flex;
  position: relative;
  font-family: 'euclid circular a', sans-serif;
  flex-direction: row;
  gap: 5px;
}

.test-label {
  font-size: 12px;
  color: #333;
  padding: 0px 8px 0px 8px;
  background-color: #f1f2f6;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  display: flex;
  align-items: center;
  font-family: 'euclid_medium', sans-serif;
  height: 23px;
}

.tests-value {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0px 8px 0px 8px;
  cursor: pointer;
  background-color: #f1f2f6;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  height: 23px;
  font-family: 'euclid circular a', sans-serif;
  transition: background-color 0.2s ease;
}

.tests-value:hover {
  background-color: #e8e9ef;
}

.tests-value svg {
  display: flex;
  height: 10px;
  width: 10px;
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.value-text {
  font-size: 12px;
  color: #4361ee;
  white-space: nowrap;
  overflow: hidden;
  font-family: 'euclid circular a', sans-serif;
}

/* Portal Container */
.dropdown-portal-container {
  animation: dropdownFadeIn 0.15s ease-out;
}

@keyframes dropdownFadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdown-menu-portal {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid #eee;
  overflow: hidden;
  min-width: 100px;
  max-width: 300px;
}

.dropdown-items-container {
  max-height: 200px;
  overflow-y: auto;
  overflow-x: hidden;

  /* Custom scrollbar */
  scrollbar-width: thin;
  scrollbar-color: #ddd #f9f9f9;
}

.dropdown-items-container::-webkit-scrollbar {
  width: 3px;
}

.dropdown-items-container::-webkit-scrollbar-track {
  background: transparent;
}

.dropdown-items-container::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 3px;
}

.dropdown-items-container::-webkit-scrollbar-thumb:hover {
  background: #bbb;
}

.search-container {
  padding: 8px;
  border-bottom: 1px solid #f1f2f6;
  background: #fafafa;
}

.search-input {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 11px;
  outline: none;
  font-family: 'euclid circular a', sans-serif;
  transition: border-color 0.2s ease;
}

.dropdown-item {
  cursor: pointer;
  font-size: 10px;
  display: block;
  padding: 8px 12px;
  line-height: 1;
  transition: background-color 0.15s ease;
}

.dropdown-item-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.dropdown-item-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.dropdown-item-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  max-width: 100%;
  font-family: 'euclid circular a', sans-serif;
}

.dropdown-item:hover {
  background-color: #f1f2f6;
}

.dropdown-item.active {
  background-color: #f1f2f6;
  color: #4361ee;
  font-weight: 500;
}

.dropdown-portal-container {
  animation: dropdownFadeIn 0.15s ease-out;
}
</style>
